import { useEffect, useRef, useState } from 'react';

import { buildChipSelection, chipsAreDefault, type ChipStates } from '@/components/PhraseChips';
import { apiClient, type FeedItem, type PhraseChip, type PhraseTokenDto } from '@/services';
import { BufferedLiveSearchPager, isAbortError } from './liveSearchPaging';

const noChipStates: ChipStates = new Map();
const noPhraseTokens: readonly PhraseTokenDto[] = [];
const noChips: readonly PhraseChip[] = [];

interface UseCardFeedOptions {
  enabled?: boolean;
  pageSize?: number;
  initialChips?: readonly PhraseChip[];
  /** The card's phrase as the matcher sees it; the corpus search takes it back with the chips. */
  phraseTokens?: readonly PhraseTokenDto[];
  /** The card's phrase as its owner wrote it, carried on each live match for the link back to the card. */
  phraseText?: string;
  /**
   * Per-chip state, keyed by lemma. Anything other than what the card stored — a word released, a
   * part of speech relaxed, a chip switched off — widens the search.
   */
  chipStates?: ChipStates;
  checkSense?: boolean;
}

interface UseCardFeedResult {
  items: FeedItem[];
  /**
   * The chips for this card — lemma and the part of speech the card gave it, as the matcher sees
   * its phrase.
   */
  chips: readonly PhraseChip[];
  /** Whether every chip still stands for what the card stored, so these are its own matches. */
  atDefault: boolean;
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  error: string;
  loadMore: () => Promise<void>;
  retry: () => void;
}

/**
 * Feeds the card's Feed panel through the Lucene-backed SearchV2 endpoint. The first request uses
 * the complete card phrase; relaxing a chip widens the same live search rather than switching to
 * the precomputed match table.
 */
export function useCardFeed(
  meaningId: string,
  {
    enabled = true,
    pageSize = 10,
    initialChips: chips = noChips,
    phraseTokens = noPhraseTokens,
    phraseText = '',
    chipStates = noChipStates,
    checkSense = false,
  }: UseCardFeedOptions = {}
): UseCardFeedResult {
  const [items, setItems] = useState<FeedItem[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(enabled);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [retryCount, setRetryCount] = useState(0);
  const retry = () => setRetryCount((count) => count + 1);
  const requestControllerRef = useRef<AbortController | null>(null);
  const livePagerRef = useRef<BufferedLiveSearchPager<FeedItem> | null>(null);

  // Mirrors `items` for the live pager continuation without reading the answer out of a state
  // updater, which React is free to run more than once.
  const itemsRef = useRef<FeedItem[]>(items);
  itemsRef.current = items;

  const atDefault = chipsAreDefault(chips, chipStates);
  const selection = buildChipSelection(chips, chipStates);
  // Effects key off the resolved selection, not the identity of the map that carries it, so a
  // re-render that reaches the same selection does not refetch.
  const queryKey = atDefault
    ? 'search:default'
    : `search:${selection.words.join(' ')}|${selection.anyWords.join(' ')}|${selection.posOverrides.join(' ')}`;

  useEffect(() => {
    requestControllerRef.current?.abort();
    livePagerRef.current = null;

    if (!enabled) {
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    requestControllerRef.current = controller;
    const isCurrent = () => requestControllerRef.current === controller && !controller.signal.aborted;

    setLoading(true);
    setError('');
    setItems([]);
    setHasMore(false);

    const pager = new BufferedLiveSearchPager<FeedItem>({
      pageSize,
      getKey: (item) => item.matchId,
      fetchWindow: (cursor, limit) =>
        apiClient.searchNewsPage(
          meaningId, phraseText, phraseTokens, selection, cursor, limit, controller.signal, checkSense),
    });
    livePagerRef.current = pager;

    pager.loadFirst((chunk) => {
      if (!isCurrent()) return;
      setItems((current) => [...current, ...chunk]);
      setLoading(false);
    })
      .then((page) => {
        if (!isCurrent()) return;
        setHasMore(page.hasMore);
      })
      .catch((caught) => {
        if (isCurrent() && !isAbortError(caught)) {
          setError('Unable to load the feed. Please try again.');
        }
      })
      .finally(() => {
        if (isCurrent()) setLoading(false);
      });

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meaningId, enabled, pageSize, queryKey, retryCount]);

  const loadMore = async () => {
    if (loadingMore || !hasMore) return;

    setLoadingMore(true);
    setError('');
    try {
      const controller = requestControllerRef.current;
      if (!controller || controller.signal.aborted) return;

      const pager = livePagerRef.current;
      if (!pager) return;
      const page = await pager.takeNext(itemsRef.current.map((item) => item.matchId));
      if (requestControllerRef.current !== controller || !page) return;

      setItems((current) => [...current, ...page.items]);
      setHasMore(page.hasMore);
    } catch (caught) {
      if (!isAbortError(caught)) setError('Unable to load more matches. Please try again.');
    } finally {
      setLoadingMore(false);
    }
  };

  return {
    items,
    chips,
    atDefault,
    loading,
    loadingMore,
    hasMore,
    error,
    loadMore,
    retry,
  };
}
